import DefaultQueryFormatter from './DefaultQueryFormatter'
import EventsQueryFormatter from './EventsQueryFormatter'
import FeaturedTopicsQueryFormatter from './FeaturedTopicsQueryFormatter'
import NewsQueryFormatter from './NewsQueryFormatter'
import QuickAccessQueryFormatter from './QuickAccessQueryFormatter'
import RealStoriesQueryFormatter from './RealStoriesQueryFormatter'
import ResourcesQueryFormatter from './ResourcesQueryFormatter'
import SearchQueryFormatter from './SearchQueryFormatter'
import ServiceProvidersQueryFormatter from './ServiceProvidersQueryFormatter'

// Re-export DefaultQueryFormatter as QueryFormatter for external use
export type QueryFormatter = DefaultQueryFormatter

// Known formatter types for documentation purposes
export type KnownQueryFormatterType =
  | 'Events'
  | 'Default'
  | 'FeaturedTopics'
  | 'Resources'
  | 'RealStories'
  | 'News'
  | 'ServiceProviders'
  | 'Search'
  | 'QuickAccess'

const instances: Record<string, DefaultQueryFormatter> = {}

/**
 * Creates and returns a query formatter for a given type
 *
 * @param type The type to construct. If an unknown type is provided, a default formatter will be returned.
 * @returns A query formatter
 */
export const QueryFormatterFactory = function (
  type: KnownQueryFormatterType | string,
): QueryFormatter {
  try {
    throwableTypeValidate(type)
  }
  catch (err) {
    console.error(err)
    type = 'Default'
  }

  if (!instances[type]) {
    instances[type] = getFormatterForType(type)
  }
  return instances[type]
}

const throwableTypeValidate = function (
  type: string,
): void {
  if (!type) {
    throw new Error(
      `QueryFormatterFactory: type is expected - input was { type: "${type}" }`,
    )
  }
}

const getFormatterForType = (type: string): DefaultQueryFormatter => {
  // console.debug('Get Query formatter for type', type)
  switch (type) {
    case 'Events':
      return new EventsQueryFormatter()
    case 'FeaturedTopics':
      return new FeaturedTopicsQueryFormatter()
    case 'Resources':
      return new ResourcesQueryFormatter()
    case 'RealStories':
      return new RealStoriesQueryFormatter()
    case 'News':
      return new NewsQueryFormatter()
    case 'ServiceProviders':
      return new ServiceProvidersQueryFormatter()
    case 'Search':
      return new SearchQueryFormatter()
    case 'QuickAccess':
      return new QuickAccessQueryFormatter()
    default:
      console.error('Query formatter NOT found for type', type)
      // console.debug('Returning a default formatter instead')
      return new DefaultQueryFormatter()
  }
}
